import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Alert,
  Divider,
  Row,
  Col
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from 'react-oidc-context';
import axios from '../../../../helpers/Axios';

import SecLogo from '../../../../utility/unnamed.png';
import SecBuilding from '../../../../assets/files/img/aaa.jpg';

const normalizeApiMessage = (raw) => {
  if (typeof raw === 'string') return raw;

  if (Array.isArray(raw)) {
    return raw.join(', ');
  }

  if (raw && typeof raw === 'object') {
    return Object.entries(raw)
      .map(([key, value]) => {
        const safeValue = Array.isArray(value) ? value.join(', ') : value;
        return `${key}: ${safeValue}`;
      })
      .join(', ');
  }

  return 'Something went wrong.';
};

const authHeaders = {
  headers: {
    'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY_AUTHENTICATION,
  },
};

export default function EnrollmentCompany() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const state = location.state || {};
  const userK = state?.keycloakProfile || auth.user?.profile || {};

  const exceptional = 
    state?.exceptional ??
    state?.details?.exceptional ??
    false;
  
  const verified = state?.verified ?? false;

  const fullName = `${userK?.given_name ?? ''} ${userK?.family_name ?? ''}`.trim();
  const parsedTin = (userK?.tin || '').replace(/-/g, '').slice(0, 9);

  const defaultValues = useMemo(() => ({
    sec_no: exceptional ? '' : state?.secNo || state?.details?.secNo || '',
    company_name: exceptional ? '' : state?.companyName || state?.details?.companyName || '',
    company_type: exceptional ? '' : state?.companyType || state?.details?.companyType || '',
    status: exceptional ? '' : state?.status || state?.details?.companyStatus || '',
    official_email: exceptional ? '' : state?.mc28_details?.official_email || '',
    mobile_number: exceptional ? '' : state?.mc28_details?.mobile_number || '',
    username: userK?.preferred_username || '',
    name: fullName,
    tin: userK?.tin || '',
    birthdate: userK?.birthdate || '',
  }), [exceptional, state, userK, fullName]);

  useEffect(() => {
    form.setFieldsValue(defaultValues);
  }, [form, defaultValues]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setAlert(null);

      if (!userK?.sub) {
        setAlert({
          type: 'error',
          message: 'Missing authenticated user profile. Please log in again.',
        });
        return;
      }

      const payload = {
        sec_no: values.sec_no,
        company_name: values.company_name,
        exceptional,
        email: userK?.email || null,
        first_name: userK?.given_name || null,
        last_name: userK?.family_name || null,
        middle_name: userK?.middlename || userK?.middle_name || null,
        mc28_email: values.official_email,
        mobile_no: values.mobile_number,
        company_type: values.company_type,
        company_status: values.status,
        tin: parsedTin,
        keycloak_id: userK?.sub || null,
        username: values.username,
        password: userK.sub, 
        birth_date: userK?.birthdate || null,
      };

      await axios.post(
        "https://api.fortisph.space/gateway/authentication/company/register/", 
        payload,
        authHeaders,
      );

      // REDIRECT BASED ON VERIFICATION STATUS
      navigate(verified ? '/user/successfully-enrolled' : '/user/under-review');

    } catch (error) {
      setAlert({
        type: "error",
        message: normalizeApiMessage(
          error.response?.data?.message ?? error.response?.data
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* ── Background Image ── */}
      <img
        src={SecBuilding}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />

      {/* ── Dark Gradient Overlay ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(100deg, rgba(0,36,22,0.93) 0%, rgba(0,50,32,0.82) 45%, rgba(0,30,18,0.65) 100%)',
        }}
      />

      {/* ── Main Canvas Layer ── */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Centered Content Wrapper */}
        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 md:p-12">
          
          {/* Centered Form Card */}
          <div
            className="w-full max-w-4xl bg-white rounded-[20px] p-6 sm:p-10 my-auto"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }}
          >
            {/* Header / Branding Inside Card */}
            <header className="flex flex-col items-center text-center mb-6 pb-6 border-b border-gray-100">
              <img
                src={SecLogo}
                alt="SEC Logo"
                style={{ height: 60, width: 'auto', marginBottom: 12 }}
              />
              <h1 className="text-xl md:text-2xl font-extrabold text-[#111827] mb-1">
                Electronic Filing and Submission Tool (eFAST)
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mb-2">
                Formerly known as Online Submission Tool (OST)
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    padding: '2px 10px',
                    borderRadius: 999,
                    background: 'rgba(15,130,105,0.1)',
                    color: '#0f8269',
                    border: '1px solid rgba(15,130,105,0.2)',
                  }}
                >
                  COMPANY ENROLLMENT
                </span>
              </div>
            </header>

            {alert && (
              <Alert
                type={alert.type}
                message={alert.message}
                closable
                onClose={() => setAlert(null)}
                style={{ marginBottom: 20, borderRadius: 10 }}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              initialValues={defaultValues}
              onFinish={handleSubmit}
              requiredMark={false}
            >
              {/* Section 1: Company Information */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-4">
                <h3 className="text-base font-bold text-gray-800 m-0">Company Information</h3>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>SEC Registration No.</span>}
                    name="sec_no"
                    rules={[
                      { required: true, message: "This field is required" },
                      { pattern: /^[A-Z0-9-]+$/, message: "Only uppercase letters, numbers and dashes allowed." }
                    ]}
                  >
                    <Input 
                      disabled={!exceptional} 
                      maxLength={16}
                      style={{ borderRadius: 8, height: 42, background: !exceptional ? '#f9fafb' : '#fff' }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Company Name</span>}
                    name="company_name"
                    rules={[{ required: true, message: "Company name is required" }]}
                  >
                    <Input 
                      disabled={!exceptional}
                      style={{ borderRadius: 8, height: 42, background: !exceptional ? '#f9fafb' : '#fff' }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Company Type</span>}
                    name="company_type"
                    rules={[{ required: true, message: "Company type is required" }]}
                  >
                    <Input
                      disabled={!exceptional}
                      style={{ borderRadius: 8, height: 42, background: !exceptional ? '#f9fafb' : '#fff' }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Company Status</span>}
                    name="status"
                    rules={[{ required: true, message: "Company status is required" }]}
                  >
                    <Input 
                      disabled={!exceptional}
                      style={{ borderRadius: 8, height: 42, background: !exceptional ? '#f9fafb' : '#fff' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <p className="text-xs text-slate-500 mb-6">
                * Details above are as declared by the company in the MC No. 28 Submission Portal of the Securities and Exchange Commission.
              </p>

              {/* Section 2: MC28 Information */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-4">
                <h3 className="text-base font-bold text-gray-800 m-0">MC28 Information</h3>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Official Email Address</span>}
                    name="official_email"
                    rules={[
                      { required: true, message: 'Official email is required' },
                      { type: 'email', message: 'Invalid email format' },
                    ]}
                  >
                    <Input style={{ borderRadius: 8, height: 42 }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Official Landline/Mobile No.</span>}
                    name="mobile_number"
                  >
                    <Input 
                      disabled={!exceptional}
                      style={{ borderRadius: 8, height: 42, background: !exceptional ? '#f9fafb' : '#fff' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Section 3: User Information */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-4">
                <h3 className="text-base font-bold text-gray-800 m-0">User Information</h3>
              </div>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Name</span>} name="name">
                    <Input disabled style={{ borderRadius: 8, height: 42, background: '#f9fafb' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>TIN</span>} name="tin">
                    <Input disabled style={{ borderRadius: 8, height: 42, background: '#f9fafb' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
                        Username <span style={{ color: '#ef4444' }}>*</span>
                      </span>
                    }
                    name="username"
                    rules={[
                      { required: true, message: 'Username is required' },
                      {
                        pattern: /^[A-Za-z0-9_#?!@$%^*-.]+$/,
                        message: 'Invalid username format.',
                      },
                    ]}
                  >
                    <Input style={{ borderRadius: 8, height: 42 }} placeholder="Enter username" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Birth Date</span>} name="birthdate">
                    <Input disabled style={{ borderRadius: 8, height: 42, background: '#f9fafb' }} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Card Footer Actions */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'space-between',
                  width: '100%',
                  marginTop: 20,
                  paddingTop: 20,
                  borderTop: '1px solid #f3f4f6',
                }}
              >
                <Link
                  to="/user/login"
                  style={{
                    color: '#0f8269',
                    fontWeight: 600,
                    fontSize: 14,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <ArrowLeftOutlined /> Back to Sign In
                </Link>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{
                    background: '#0f8269',
                    borderColor: '#0f8269',
                    borderRadius: 10,
                    height: 44,
                    padding: '0 32px',
                    fontWeight: 600,
                    fontSize: 15,
                    marginLeft: 'auto', // Ensures it stays pinned to the right edge
                  }}
                >
                  Submit
                </Button>
              </div>
            </Form>
          </div>

        </div>
      </div>
    </div>
  );
}